import { FormEvent, useEffect, useState, type ReactNode } from 'react';
import { Banknote, Bluetooth, CreditCard, ExternalLink, Power, QrCode, RotateCcw, Settings, Smartphone, WalletCards } from 'lucide-react';
import { useApiClient } from '../lib/apiClient';
import { useOrgFormatting } from '../hooks/useOrgFormatting';

type PosData = {
  transactions: Array<{ id: string; type: string; method: string; amount: number | string; status: string; externalReference?: string | null; createdAt: string }>;
  drawers: Array<{ id: string; locationId?: string | null; openingCash: number | string; closingCash?: number | string | null; status: string; openedAt: string }>;
  exports: Array<{ id: string; rangeStart: string; rangeEnd: string; status: string; exportFormat: string; createdAt: string }>;
};

const transactionTypes = ['DEPOSIT', 'PAYMENT', 'REFUND', 'NO_SHOW_FEE'] as const;
const paymentMethods = ['CARD', 'CASH', 'EXTERNAL'] as const;

export default function PosPaymentsPage() {
  const { fetchJson } = useApiClient();
  const { formatMoney: fmtMoney } = useOrgFormatting();
  // Coerce string|number inputs and format in the org locale + currency.
  const formatMoney = (value: number | string | null | undefined) => fmtMoney(Number(value || 0));
  const [data, setData] = useState<PosData | null>(null);
  const [error, setError] = useState('');
  const [transaction, setTransaction] = useState({ type: 'PAYMENT', method: 'CARD', amount: '', externalReference: '' });
  const [drawer, setDrawer] = useState({ locationId: '', openingCash: '0' });

  async function load() {
    try {
      setError('');
      setData(await fetchJson<PosData>('/pos'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load POS data.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function recordTransaction(event: FormEvent) {
    event.preventDefault();
    await fetchJson('/pos/transactions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: transaction.type,
        method: transaction.method,
        amount: Number(transaction.amount || 0),
        externalReference: transaction.externalReference || undefined,
      }),
    });
    setTransaction({ type: 'PAYMENT', method: 'CARD', amount: '', externalReference: '' });
    await load();
  }

  async function openDrawer(event: FormEvent) {
    event.preventDefault();
    await fetchJson('/pos/cash-drawers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        locationId: drawer.locationId || undefined,
        openingCash: Number(drawer.openingCash || 0),
      }),
    });
    setDrawer({ locationId: '', openingCash: '0' });
    await load();
  }

  async function createExport() {
    const end = new Date();
    const start = new Date(end);
    start.setDate(end.getDate() - 30);
    await fetchJson('/pos/accounting-exports', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rangeStart: start.toISOString(), rangeEnd: end.toISOString(), exportFormat: 'CSV' }),
    });
    await load();
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Payments and POS</p>
          <h1 className="text-2xl font-semibold text-slate-900">Deposits, payments, refunds, and cash drawers</h1>
        </div>
        <button type="button" onClick={() => void load()} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium"><RotateCcw size={16} /> Refresh</button>
      </div>
      {error ? <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <form onSubmit={recordTransaction} className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-sm font-semibold text-slate-900">Record POS transaction</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <select value={transaction.type} onChange={(event) => setTransaction((current) => ({ ...current, type: event.target.value }))} className="rounded-md border border-slate-300 px-3 py-2 text-sm">
              {transactionTypes.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
            <select value={transaction.method} onChange={(event) => setTransaction((current) => ({ ...current, method: event.target.value }))} className="rounded-md border border-slate-300 px-3 py-2 text-sm">
              {paymentMethods.map((method) => <option key={method} value={method}>{method}</option>)}
            </select>
            <input value={transaction.amount} onChange={(event) => setTransaction((current) => ({ ...current, amount: event.target.value }))} placeholder="Amount" type="number" min="0" step="0.01" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
            <input value={transaction.externalReference} onChange={(event) => setTransaction((current) => ({ ...current, externalReference: event.target.value }))} placeholder="Receipt or terminal reference" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
          </div>
          <button className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white">Record transaction</button>
        </form>

        <form onSubmit={openDrawer} className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-sm font-semibold text-slate-900">Open cash drawer</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <input value={drawer.locationId} onChange={(event) => setDrawer((current) => ({ ...current, locationId: event.target.value }))} placeholder="Location id (optional)" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
            <input value={drawer.openingCash} onChange={(event) => setDrawer((current) => ({ ...current, openingCash: event.target.value }))} placeholder="Opening cash" type="number" min="0" step="0.01" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
          </div>
          <div className="flex flex-wrap gap-2">
            <button className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white">Open drawer</button>
            <button type="button" onClick={() => void createExport()} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700">Create accounting export</button>
          </div>
        </form>
      </div>

      <section className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Card terminals</div>
              <p className="text-sm text-slate-500">Request, register, configure, and troubleshoot terminal devices by location.</p>
            </div>
            <button type="button" className="inline-flex items-center gap-2 rounded-md bg-sky-600 px-3 py-2 text-sm font-semibold text-white">
              <Settings size={15} /> Request terminal
            </button>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <TerminalCard name="BBPOS WisePad 3" status="Ready" detail="Bluetooth paired to front desk iPad" icon={<Bluetooth size={16} />} />
            <TerminalCard name="Stripe WisePOS E" status="Online" detail="Clinic A checkout desk, reader code WPE-104" icon={<Power size={16} />} />
            <TerminalCard name="Mobile Tap to Pay" status="Guided setup" detail="iOS permission and Bluetooth guidance for Pabau Go." icon={<Smartphone size={16} />} />
            <TerminalCard name="Troubleshooting" status="Checklist" detail="Network, battery, pairing, location, and reader registration checks." icon={<QrCode size={16} />} />
          </div>
          <div className="mt-4 rounded-lg border border-sky-100 bg-sky-50 p-3">
            <label className="flex items-center justify-between gap-3 text-sm font-medium text-slate-800">
              Card terminal tipping
              <input type="checkbox" defaultChecked className="h-4 w-4 rounded border-slate-300 text-sky-600" />
            </label>
            <p className="mt-1 text-xs text-slate-500">Show tip prompts on supported readers before checkout completion.</p>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Wallets and payouts</div>
              <p className="text-sm text-slate-500">Location wallets, bank accounts, payout settings, and payment activity.</p>
            </div>
            <button type="button" className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700">
              <ExternalLink size={15} /> Stripe dashboard
            </button>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <WalletPanel icon={<WalletCards size={16} />} title="Main wallet" detail="Maniera Medical - New York, USD, daily payout." />
            <WalletPanel icon={<Banknote size={16} />} title="Bank accounts" detail="Operating ****2184, deposits verified, next payout 2 days." />
            <WalletPanel icon={<CreditCard size={16} />} title="Activity" detail="Charges, refunds, disputes, terminal payments, and payout detail." />
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {['New York wallet', 'Boston wallet', 'Miami wallet', 'Insurance clearing wallet'].map((wallet) => (
              <label key={wallet} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700">
                {wallet}
                <input type="checkbox" defaultChecked={wallet !== 'Insurance clearing wallet'} className="h-4 w-4 rounded border-slate-300 text-sky-600" />
              </label>
            ))}
          </div>
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="POS transactions" rows={(data?.transactions || []).map((row) => [row.type, `${row.method} - ${formatMoney(row.amount)} - ${row.status}`])} />
        <Panel title="Cash drawer reconciliation" rows={(data?.drawers || []).map((row) => [row.locationId || 'Unscoped drawer', `${formatMoney(row.openingCash)} opening - ${row.status}`])} />
        <Panel title="Accounting exports" rows={(data?.exports || []).map((row) => [row.exportFormat, `${new Date(row.rangeStart).toLocaleDateString()} to ${new Date(row.rangeEnd).toLocaleDateString()} - ${row.status}`])} />
      </div>
    </div>
  );
}

function Panel({ title, rows }: { title: string; rows: Array<[string, string]> }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><CreditCard size={16} /> {title}</div>
      <div className="mt-3 divide-y divide-slate-100">
        {rows.length ? rows.map(([label, value], index) => (
          <div key={`${label}-${index}`} className="py-2 text-sm">
            <div className="font-medium text-slate-700">{label}</div>
            <div className="text-slate-500">{value}</div>
          </div>
        )) : <div className="py-3 text-sm text-slate-500">No rows yet.</div>}
      </div>
    </div>
  );
}

function TerminalCard({ name, status, detail, icon }: { name: string; status: string; detail: string; icon: ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <span className="text-sky-600">{icon}</span>
          {name}
        </div>
        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">{status}</span>
      </div>
      <p className="mt-2 text-xs leading-5 text-slate-500">{detail}</p>
    </div>
  );
}

function WalletPanel({ icon, title, detail }: { icon: ReactNode; title: string; detail: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <span className="text-sky-600">{icon}</span>
        {title}
      </div>
      <p className="mt-2 text-xs leading-5 text-slate-500">{detail}</p>
    </div>
  );
}
